import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { environment } from '../../../environments/environment';
import { AuthService } from '../services/auth.service';
import { ImageUsageService } from './image-usage.service';
import { ToastService } from './toast.service';

export interface OCRResult {
  text: string;
  confidence?: number;
  language?: string;
}

@Injectable({
  providedIn: 'root',
})
export class OCRService {
  constructor(
    private http: HttpClient,
    private toastService: ToastService,
    private authService: AuthService,
    private imageUsageService: ImageUsageService
  ) {}

  /**
   * Capture photo and extract text using backend OCR API
   */
  async captureAndReadText(
    targetLanguage: string,
    onImageCaptured?: () => void
  ): Promise<OCRResult | null> {
    try {
      // 🔒 SECURITY: Check image limit before capturing
      const canProcess = await this.imageUsageService.checkImageLimit();
      if (!canProcess) {
        console.log('❌ Image limit exceeded, cannot capture photo');
        return null;
      }

      await this.ensurePermissions();
      // Capture photo
      const photo = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
      });

      if (!photo.dataUrl) {
        throw new Error('No photo captured');
      }

      // Call the callback to show loading state after image is captured
      if (onImageCaptured) {
        onImageCaptured();
      }

      // Extract text using backend OCR API (resize to reduce payload)
      const optimized = await this.reduceImageSize(photo.dataUrl, 800, 0.5);
      const result = await this.extractTextFromImage(
        optimized || photo.dataUrl,
        targetLanguage
      );

      // ✅ SUCCESS: Increment image usage counter
      if (result && result.text) {
        await this.imageUsageService.incrementImageUsage();
      }

      return result;
    } catch (error: any) {
      // Swallow cancelation without toast
      if (this.isCancelError(error)) {
        return null;
      }
      console.error('Error capturing photo:', error);
      this.toastService.showError('Failed to capture photo. Please try again.');
      return null;
    }
  }

  /**
   * Prompt user to choose Camera or Gallery (native prompt) and extract text
   */
  async promptAndReadText(targetLanguage: string): Promise<OCRResult | null> {
    try {
      await this.ensurePermissions();
      if (Capacitor.getPlatform() === 'android') {
        // On some Android devices, Prompt UI can be blocked. Use pickImages API.
        try {
          const picked = await Camera.pickImages({ quality: 90, limit: 1 });
          const first = picked?.photos?.[0];
          if (!first) return null;
          const dataUrl = await this.toDataUrl(
            first.webPath || (first as any).path || undefined
          );
          if (!dataUrl) return null;
          const optimized = await this.reduceImageSize(dataUrl, 800, 0.5);
          return await this.extractTextFromImage(
            optimized || dataUrl,
            targetLanguage
          );
        } catch (e) {
          // Fallback to getPhoto Prompt
        }
      }

      const isNative = Capacitor.isNativePlatform();
      const photo = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: isNative ? CameraSource.Prompt : CameraSource.Photos,
        webUseInput: true,
        saveToGallery: false,
      });

      if (!photo?.dataUrl) {
        return null; // user canceled
      }

      const optimizedPrompt = await this.reduceImageSize(
        photo.dataUrl,
        800,
        0.5
      );
      return await this.extractTextFromImage(
        optimizedPrompt || photo.dataUrl,
        targetLanguage
      );
    } catch (err: any) {
      // Swallow cancellation errors
      const message = (err && err.message) || '';
      if (message && /cancel/i.test(message)) {
        return null;
      }
      console.error('Error prompting photo:', err);
      this.toastService.showError('Failed to open image picker.');
      return null;
    }
  }

  private async ensurePermissions(): Promise<void> {
    try {
      const status = await Camera.checkPermissions();
      if (status.camera !== 'granted' || status.photos !== 'granted') {
        const req = await Camera.requestPermissions({
          permissions: ['camera', 'photos'] as any,
        });
        if (req.camera !== 'granted') {
          throw new Error('Camera permission not granted');
        }
      }
    } catch (e) {
      console.warn('Camera permission flow error:', e);
      throw e;
    }
  }

  private async toDataUrl(webPath?: string | null): Promise<string | null> {
    try {
      if (!webPath) return null;
      const res = await fetch(webPath);
      const blob = await res.blob();
      return await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string) || '');
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.warn('toDataUrl failed:', e);
      return null;
    }
  }

  private async reduceImageSize(
    dataUrl: string,
    maxSize: number,
    quality: number
  ): Promise<string | null> {
    try {
      const img = document.createElement('img');
      const loaded: Promise<void> = new Promise((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = (e) => reject(e);
      });
      img.src = dataUrl;
      await loaded;
      let width = (img as any).width as number;
      let height = (img as any).height as number;
      if (!width || !height) return dataUrl;
      if (width <= maxSize && height <= maxSize) return dataUrl;
      if (width > height) {
        height = Math.round((height * maxSize) / width);
        width = maxSize;
      } else {
        width = Math.round((width * maxSize) / height);
        height = maxSize;
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return dataUrl;
      ctx.drawImage(img, 0, 0, width, height);
      return canvas.toDataURL('image/jpeg', quality);
    } catch (e) {
      console.warn('reduceImageSize failed:', e);
      return dataUrl;
    }
  }

  /**
   * Extract text from image using backend OCR API
   */
  private async extractTextFromImage(
    imageDataUrl: string,
    targetLanguage: string
  ): Promise<OCRResult | null> {
    try {
      // Convert data URL to base64
      const base64Data = imageDataUrl.split(',')[1];

      const token = await this.authService.getAuthToken().catch(() => null);
      const headers = new HttpHeaders(
        token
          ? {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            }
          : { 'Content-Type': 'application/json' }
      );

      const requestBody = {
        image: base64Data,
        targetLanguage: targetLanguage,
      };

      const response = await this.http
        .post<any>(`${environment.apiUrl}/api/ocr/extract-text`, requestBody, {
          headers,
          withCredentials: true, // allow cookie-based auth on web
        })
        .toPromise();

      // Check if response has text and it's not empty
      if (response && response.text && response.text.trim().length > 0) {
        return {
          text: response.text.trim(),
          language: targetLanguage,
        };
      }

      // If response exists but text is empty, provide a user-friendly message
      if (response && response.success === false) {
        throw new Error(
          response.error || 'No text could be extracted from this image'
        );
      }

      // If text is empty or missing, provide helpful feedback
      throw new Error(
        'No text found in image. Please ensure the image contains readable text.'
      );
    } catch (error: any) {
      console.error('Error extracting text from image:', error);

      // Handle specific error cases
      if (error?.status === 413) {
        this.toastService.showError(
          'Image is too large. Please try a smaller image or take a new photo.'
        );
        return null;
      }

      // Extract the most user-friendly error message
      let backendMsg =
        (error && error.error && (error.error.error || error.error.message)) ||
        (error && error.message) ||
        'Failed to read text from image. Please try again.';

      // Provide more helpful messages for common cases
      if (
        backendMsg.includes('No text found') ||
        backendMsg.includes('No text extracted')
      ) {
        backendMsg =
          'No text found in image. Please ensure the image contains clear, readable text.';
      } else if (backendMsg.includes('too large')) {
        backendMsg =
          'Image is too large. Please try a smaller image or take a new photo.';
      }

      // Show the most specific message we have
      this.toastService.showError(backendMsg);
      return null;
    }
  }

  /**
   * Select image from gallery and extract text
   */
  async selectAndReadText(
    targetLanguage: string,
    onImageSelected?: () => void
  ): Promise<OCRResult | null> {
    try {
      // 🔒 SECURITY: Check image limit before selecting
      const canProcess = await this.imageUsageService.checkImageLimit();
      if (!canProcess) {
        console.log('❌ Image limit exceeded, cannot select photo');
        return null;
      }

      // Select photo from gallery
      const photo = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Photos,
      });

      if (!photo.dataUrl) {
        throw new Error('No photo selected');
      }

      // Call the callback to show loading state after image is selected
      if (onImageSelected) {
        onImageSelected();
      }

      // Extract text using backend OCR API
      const result = await this.extractTextFromImage(
        photo.dataUrl,
        targetLanguage
      );

      // ✅ SUCCESS: Increment image usage counter
      if (result && result.text) {
        await this.imageUsageService.incrementImageUsage();
      }

      return result;
    } catch (error: any) {
      // Swallow cancellation silently
      if (this.isCancelError(error)) {
        return null;
      }
      console.error('Error selecting photo:', error);
      this.toastService.showError('Failed to select photo. Please try again.');
      return null;
    }
  }

  // Detect user cancellation errors from Capacitor Camera or browser picker
  private isCancelError(err: any): boolean {
    const msg = (err && (err.message || err.toString?.())) || '';
    return /cancel|canceled|cancelled|user.*dismissed|No\s*image\s*picked/i.test(
      msg
    );
  }
}
